/**
 * Record type helpers for parsing and inferring record types from reference numbers
 *
 * NOTE: Keep in sync with backend version at app/models/concerns/has_reference_number.rb
 */

// Mapping of record prefixes to their model class names
// Based on app/models/concerns/has_reference_number.rb
const PREFIX_TO_CLASS: Record<string, string> = {
  // Core records
  C: 'Competitor',
  E: 'Epic',
  R: 'Release',
  S: 'Initiative',
  G: 'StrategicImperative',
  M: 'BusinessModel',

  // Ideas
  I: 'Ideas::Idea',
  T: 'Ideas::IdeaTheme',
  Y: 'Ideas::IdeaSession',
  O: 'Ideas::IdeaOrganization',

  // Discovery
  FI: 'Discovery::File',
  IN: 'Discovery::Interview',
  ST: 'Discovery::Study',

  // Agile
  SP: 'Iteration',
  PI: 'ProgramIncrement',

  // Other
  KR: 'KeyResult',
  N: 'Page',
  P: 'Persona',
  POLL: 'Polls::Poll',

  // Special cases with nil prefix (handled separately)
  // Feature: nil prefix
  // Requirement: nil prefix, but different pattern
};

/**
 * Infers the record type from a record ID/reference number
 *
 * Format can be:
 * - PROJECT-NUMBER (Feature has nil prefix)
 * - PROJECT-PREFIX-NUMBER (top level records like Epic, Initiative, etc.)
 * - PROJECT-PREFIX-NUMBER-PREFIX-NUMBER (nested records like KeyResult)
 * - PROJECT-NUMBER-NUMBER (special case for Requirement)
 *
 * @param recordId - The record ID to parse (e.g., "DEVELOP-123", "DEVELOP-E-456")
 * @returns The record type class name or null if unable to determine
 */
export function inferRecordType(recordId: string): string | null {
  if (!recordId) return null;

  const parts = recordId.split('-');

  // Handle different formats based on the number of parts
  if (parts.length === 2) {
    // Format: PROJECT-NUMBER (Feature has nil prefix)
    return 'Feature';
  } else if (parts.length === 3 && /^\d+$/.test(parts[1])) {
    // Special case: PROJECT-NUMBER-NUMBER (Requirement)
    // Check if the second part is a number
    return 'Requirement';
  } else if (parts.length >= 3) {
    // Format: PROJECT-PREFIX-NUMBER or PROJECT-PREFIX-NUMBER-PREFIX-NUMBER
    // The model prefix is always the second-to-last part
    const modelPrefix = parts[parts.length - 2];
    return PREFIX_TO_CLASS[modelPrefix] || null;
  }

  return null;
}
